import { rgba } from "polished"
import styled, { keyframes } from "styled-components"
import colors from "styles/colors"
import mediaQueries from "styles/mediaQueries"
import motionTokens from "styles/motionTokens"

import { BUTTONS_ALPHA, BUTTONS_ALPHA_HOVER } from "./styled"

export const ZOOM_BUTTON_ALPHA = 0.6
export const ZOOM_BUTTON_ALPHA_HOVER = 0.48
const OVERLAY_CONTENT_MAX_WIDTH = 1120

const overlayScaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(${motionTokens.press.medium});
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

export const OverlayViewport = styled.div`
  align-items: center;
  display: flex;
  min-height: 100%;
  justify-content: center;
  position: relative;
  width: 100%;
  padding: 0 24px;

  ${mediaQueries.tablet} {
    padding: 0 48px;
  }

  ${mediaQueries.desktop} {
    padding: 0 120px;
  }

  ${mediaQueries.largeDesktop} {
    padding: 0 160px;
  }

  ${mediaQueries.extraLargeDesktop} {
    padding: 0 360px;
  }
`

export const OverlayContent = styled.div`
  color: ${colors.white};
  display: grid;
  gap: 24px;
  max-height: min(90vh, 960px);
  max-width: min(96vw, ${OVERLAY_CONTENT_MAX_WIDTH}px);
  overflow: hidden;
  padding: 24px;
  position: relative;
  width: 100%;
  animation: ${overlayScaleIn} ${motionTokens.duration.medium}ms
    ${motionTokens.easing.standard};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const OverlayToolbar = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  width: 100%;
`

export const OverlayCounter = styled.span`
  align-items: center;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`

export const OverlayCloseButton = styled.button`
  align-items: center;
  background: ${rgba(colors.black, BUTTONS_ALPHA)};
  border: none;
  border-radius: 999px;
  color: ${colors.white};
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 16px;
  transition: background ${motionTokens.duration.short}ms
    ${motionTokens.easing.standard};
  top: 16px;
  z-index: 2;

  svg {
    transition: transform ${motionTokens.duration.short}ms
      ${motionTokens.easing.standard};
    transform-origin: center;
    will-change: transform;
  }

  &:hover,
  &:focus-visible {
    background: ${rgba(colors.black, BUTTONS_ALPHA_HOVER)};
    color: ${colors.white};
    outline: none;
    svg {
      transform: scale(${motionTokens.scale.medium});
    }
  }

  &:active {
    svg {
      transform: scale(${motionTokens.press.medium});
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:focus-visible,
    &:active {
      svg {
        transform: none;
      }
    }
  }
`

export const OverlayFigure = styled.figure`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
  margin: 0;
  position: relative;
  width: 100%;
`

export const OverlayImage = styled.img`
  border-radius: 8px;
  height: auto;
  max-height: min(75vh, 720px);
  object-fit: contain;
  width: 100%;
`

export const OverlayCaption = styled.figcaption`
  color: ${colors.white};
  font-size: 14px;
  text-align: center;
`

const overlayPlacement = {
  left: "left: 12px;",
  right: "right: 12px;"
} as const

export const OverlayNavigationWrapper = styled.div<{
  $placement: "left" | "right"
}>`
  align-items: center;
  bottom: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  ${({ $placement }) => overlayPlacement[$placement]};
  display: flex;
`

export const OverlayNavigationButton = styled.button`
  align-items: center;
  background: ${rgba(colors.black, ZOOM_BUTTON_ALPHA)};
  border: none;
  border-radius: 999px;
  color: ${colors.white};
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 12px 18px;
  pointer-events: auto;
  transition: background ${motionTokens.duration.short}ms
    ${motionTokens.easing.standard};

  svg {
    transition: transform ${motionTokens.duration.short}ms
      ${motionTokens.easing.standard};
    transform-origin: center;
    will-change: transform;
  }

  &:hover,
  &:focus-visible {
    background: ${rgba(colors.black, ZOOM_BUTTON_ALPHA_HOVER)};
    svg {
      transform: scale(${motionTokens.scale.medium});
    }
  }

  &:active {
    svg {
      transform: scale(${motionTokens.press.medium});
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:focus-visible,
    &:active {
      svg {
        transform: none;
      }
    }
  }
`

export const OverlayNavigationIcon = styled.span`
  display: grid;
  place-items: center;
`

export const OverlayNavigationLabel = styled.span`
  display: none;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;

  ${mediaQueries.tablet} {
    display: inline;
  }
`
